/**
 * Play Integrity Plugin - Google Play Integrity API Integration
 *
 * Uses the Standard Play Integrity API to verify app authenticity, device
 * integrity, and protect against tampering, rooting, and unauthorized access.
 *
 * Flow:
 * 1. prepareTokenProvider() — warm up on app launch (async, ~few seconds)
 * 2. requestToken(requestHash) — on-demand token for sensitive operations
 * 3. Server verifies the token via Google Play Integrity REST API
 *
 * @author TotalAccess Development Team
 * @date August 2026
 */

import { registerPlugin } from '@capacitor/core';

export interface PlayIntegrityAvailability {
  available: boolean;
  tokenProviderReady: boolean;
  isPreparing: boolean;
}

export interface PrepareResult {
  prepared: boolean;
  alreadyReady?: boolean;
  alreadyPreparing?: boolean;
  errorCode?: number;
  errorMessage?: string;
}

export interface TokenResult {
  success: boolean;
  token?: string;
  errorCode?: number;
  errorMessage?: string;
}

export interface TokenProviderStatus {
  ready: boolean;
  preparing: boolean;
  preparedAt: number;
  isStale: boolean;
}

/**
 * Decoded integrity verdict returned by Google Play Integrity API.
 * This is the structure of the tokenPayloadExternal response.
 */
export interface IntegrityVerdict {
  requestDetails?: {
    requestPackageName: string;
    requestHash?: string;
    nonce?: string;
    timestampMillis: string;
  };
  appIntegrity?: {
    appRecognitionVerdict: string;
    package_name?: string;
    version_code?: string;
    certificateSha256Digest?: string[];
  };
  deviceIntegrity?: {
    deviceRecognitionVerdict?: string[];
    deviceAttributes?: Record<string, string>;
  };
  accountDetails?: {
    appLicensingVerdict: string;
  };
  environmentDetails?: {
    appAccessRiskVerdict?: Record<string, unknown>;
    playProtectVerdict?: string;
  };
}

export interface PlayIntegrityPlugin {
  isAvailable(): Promise<PlayIntegrityAvailability>;
  prepareTokenProvider(options: { cloudProjectNumber: string }): Promise<PrepareResult>;
  requestToken(options: { requestHash: string }): Promise<TokenResult>;
  requestTokenForData(options: { requestData: string }): Promise<TokenResult>;
  isTokenProviderReady(): Promise<TokenProviderStatus>;
}

// Web fallback — Play Integrity is Android-only
class PlayIntegrityWeb implements PlayIntegrityPlugin {
  async isAvailable(): Promise<PlayIntegrityAvailability> {
    return { available: false, tokenProviderReady: false, isPreparing: false };
  }

  async prepareTokenProvider(_options: { cloudProjectNumber: string }): Promise<PrepareResult> {
    return { prepared: false };
  }

  async requestToken(_options: { requestHash: string }): Promise<TokenResult> {
    return { success: false, errorMessage: 'Play Integrity not available on web' };
  }

  async requestTokenForData(_options: { requestData: string }): Promise<TokenResult> {
    return { success: false, errorMessage: 'Play Integrity not available on web' };
  }

  async isTokenProviderReady(): Promise<TokenProviderStatus> {
    return { ready: false, preparing: false, preparedAt: 0, isStale: true };
  }
}

const PlayIntegrityNative = registerPlugin<PlayIntegrityPlugin>('PlayIntegrity', {
  web: () => Promise.resolve(new PlayIntegrityWeb()),
});

export const PlayIntegrity = PlayIntegrityNative;

export default PlayIntegrity;
